import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["outlineColor", "outlineWidth", "outlineOpacity", "fillOpacity", "fill", "size", "volume"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import memoize from 'moize';
import { mapStyle } from '@gisatcz/ptr-utils';
import flip from '@turf/flip';
import constants from '../../../constants';
import styleUtils from '../../../utils/style';

/**
 * @param feature {GeoJSONFeature}
 * @param defaultStyleObject {Object} Panther style definition
 * @return {Object}
 */
function getFeatureDefaultStyle(feature, defaultStyleObject) {
  return getFeatureLeafletStyle(feature, defaultStyleObject);
}

/**
 * @param feature {GeoJSONFeature}
 * @param defaultStyleObject {Object} Panther style definition
 * @param accentedStyleObject {Object} Panther style definition
 * @return {Object}
 */
function getFeatureAccentedStyle(feature, defaultStyleObject, accentedStyleObject) {
  var style = _objectSpread(_objectSpread({}, defaultStyleObject), accentedStyleObject);
  return getFeatureLeafletStyle(feature, style);
}

/**
 * @param feature {GeoJSONFeature}
 * @param style {Object} Panther style definition
 * @return {Object} Leaflet style definition
 */
function getFeatureLeafletStyle(feature, style) {
  var outlineColor = style.outlineColor,
    outlineWidth = style.outlineWidth,
    outlineOpacity = style.outlineOpacity,
    fillOpacity = style.fillOpacity,
    fill = style.fill,
    size = style.size,
    volume = style.volume,
    finalStyle = _objectWithoutProperties(style, _excluded);
  finalStyle.color = style.outlineColor ? style.outlineColor : null;
  finalStyle.weight = style.outlineWidth ? style.outlineWidth : 0;
  finalStyle.opacity = style.outlineOpacity || style.outlineOpacity === 0 ? style.outlineOpacity : 1;
  finalStyle.fillOpacity = style.fillOpacity || style.fillOpacity === 0 ? style.fillOpacity : 1;
  finalStyle.fillColor = style.fill;
  if (!style.fill) {
    finalStyle.fillColor = null;
    finalStyle.fillOpacity = 0;
  }
  if (!style.outlineColor || !style.outlineWidth) {
    finalStyle.color = null;
    finalStyle.opacity = 0;
    finalStyle.weight = 0;
  }

  // for point features, set radius
  if (feature.geometry.type === 'Point' || feature.geometry.type === 'MultiPoint') {
    if (style.size) {
      finalStyle.radius = style.size / 2;
    } else if (style.volume) {
      finalStyle.radius = Math.sqrt(style.volume / Math.PI);
    }
  }
  return finalStyle;
}

/**
 * Leaflet requires coordinates in different order than GeoJSON standard
 * @param feature {GeoJSONFeature}
 */
var convertCoordinatesMemo = memoize(function (feature) {
  // TODO do we need turf for this?
  var flippedFeature = flip(feature);
  return flippedFeature && flippedFeature.geometry && flippedFeature.geometry.coordinates;
});
var getDefaultStyle = function getDefaultStyle(feature, styleDefinition) {
  var defaultStyleObject = styleUtils.getDefaultStyleObject(feature, styleDefinition);
  return getFeatureDefaultStyle(feature, defaultStyleObject);
};
var calculateStyle = function calculateStyle(feature, styleDefinition, hoveredStyleDefinition, selected, selectedStyleDefinition, selectedHoveredStyleDefinition) {
  // Prepare default style
  var defaultStyleObject = styleUtils.getDefaultStyleObject(feature, styleDefinition);
  var defaultStyle = getFeatureDefaultStyle(feature, defaultStyleObject);

  // Prepare hovered style
  var hoveredStyleObject = null;
  var hoveredStyle = null;
  if (hoveredStyleDefinition) {
    hoveredStyleObject = hoveredStyleDefinition === 'default' ? constants.vectorFeatureStyle.hovered : hoveredStyleDefinition;
    hoveredStyle = getFeatureAccentedStyle(feature, defaultStyleObject, hoveredStyleObject);
  }

  // Prepare selected and selected hovered style, if selected
  var selectedStyle = null;
  var selectedHoveredStyle = null;
  if (selected) {
    var selectedHoveredStyleObject = null;
    if (selectedStyleDefinition) {
      selectedStyle = getFeatureAccentedStyle(feature, defaultStyleObject, styleUtils.getSelectedStyleObject(selectedStyleDefinition));
    }
    if (selectedHoveredStyleDefinition) {
      selectedHoveredStyleObject = selectedHoveredStyleDefinition === 'default' ? constants.vectorFeatureStyle.selectedHovered : selectedHoveredStyleDefinition;
      selectedHoveredStyle = getFeatureAccentedStyle(feature, defaultStyleObject, selectedHoveredStyleObject);
    }
  }
  return {
    "default": defaultStyle,
    hovered: hoveredStyle,
    selected: selectedStyle,
    selectedHovered: selectedHoveredStyle
  };
};
var calculateStylesMemo = memoize(calculateStyle);

/**
 * Prepare element style by shape
 * @param leafletStyle {Object} Leaflet style definition
 * @return {Object} calculated style object
 */
var getMarkerShapeCssStyle = function getMarkerShapeCssStyle(leafletStyle) {
  switch (leafletStyle.shape) {
    case 'square':
      return getMarkerShapeSquareStyle(leafletStyle);
    case 'diamond':
      return getMarkerShapeSquareStyle(leafletStyle, 45);
    case 'circle':
    default:
      return getMarkerShapeCircleStyle(leafletStyle);
  }
};

/**
 * @param leafletStyle {Object} Leaflet style definition
 * @return {Object} calculated style object
 */
var getMarkerShapeCircleStyle = function getMarkerShapeCircleStyle(leafletStyle) {
  return getMarkerShapeSquareStyle(leafletStyle, null, leafletStyle.radius);
};

/**
 * @param leafletStyle {Object} Leaflet style definition
 * @param rotation {number}
 * @param borderRadius {number}
 * @return {Object} calculated style object
 */
var getMarkerShapeSquareStyle = function getMarkerShapeSquareStyle(leafletStyle, rotation, borderRadius) {
  var style = {};
  if (leafletStyle.radius) {
    style.width = leafletStyle.radius * 2 + 'px';
    style.height = leafletStyle.radius * 2 + 'px';
  }
  if (leafletStyle.fillColor) {
    if (leafletStyle.fillOpacity && leafletStyle.fillOpacity !== 1) {
      var rgb = mapStyle.hexToRgb(leafletStyle.fillColor);
      style['backgroundColor'] = "rgba(".concat(rgb.r, ",").concat(rgb.g, ",").concat(rgb.b, ",").concat(leafletStyle.fillOpacity, ")");
    } else {
      style['backgroundColor'] = leafletStyle.fillColor;
    }
  }
  if (leafletStyle.color) {
    if (leafletStyle.opacity && leafletStyle.opacity !== 1) {
      var _rgb = mapStyle.hexToRgb(leafletStyle.color);
      style['borderColor'] = "rgba(".concat(_rgb.r, ",").concat(_rgb.g, ",").concat(_rgb.b, ",").concat(leafletStyle.opacity, ")");
    } else {
      style['borderColor'] = leafletStyle.color;
    }
  }
  if (leafletStyle.weight) {
    style['borderStyle'] = 'solid';
    style['borderWidth'] = leafletStyle.weight + 'px';
  } else {
    style['borderWidth'] = 0;
  }
  if (borderRadius) {
    style['borderRadius'] = borderRadius + 'px';
  }
  if (rotation) {
    style.transform = 'rotate(' + rotation + 'deg)';
  }
  return style;
};

/**
 * Prepare element style
 * @param leafletStyle {Object} Leaflet style definition
 * @param leafletStyle.color {string} hex code of outline color
 * @param leafletStyle.fillColor {string} hex code of fill color
 * @param leafletStyle.fillOpacity {number} range from 0 to 1, where 0 is transparent and 1 is fully opaque
 * @param leafletStyle.icon {string} icon id
 * @param leafletStyle.iconFill {string} hex code of icon fill color
 * @param leafletStyle.opacity {number} Outline opacity. range from 0 to 1, where 0 is transparent and 1 is fully opaque
 * @param leafletStyle.radius {number} Shape radius is a half of shape height/width
 * @param leafletStyle.shape {string} shape id
 * @param leafletStyle.weight {number} outline width
 * @return {Object} calculated style object suitable for SVG
 */
var getMarkerShapeSvgStyle = function getMarkerShapeSvgStyle(leafletStyle) {
  var style = {};
  if (leafletStyle.radius) {
    var size = leafletStyle.radius * 2 + (leafletStyle.weight || 0);
    style.width = size + 'px';
    style.height = size + 'px';
  }
  if (leafletStyle.fillColor) {
    style.fill = leafletStyle.fillColor;
  }
  if (leafletStyle.fillOpacity || leafletStyle.fillOpacity === 0) {
    style.fillOpacity = leafletStyle.fillOpacity;
  }
  if (leafletStyle.color) {
    style.stroke = leafletStyle.color;
  }
  if (leafletStyle.opacity) {
    style.strokeOpacity = leafletStyle.opacity;
  }
  if (leafletStyle.weight) {
    style.strokeWidth = leafletStyle.weight + 'px';
  } else {
    style.strokeWidth = 0;
  }
  if (leafletStyle.iconFill) {
    style.iconFill = leafletStyle.iconFill;
  }
  return style;
};
export default {
  calculateStyle: calculateStyle,
  calculateStylesMemo: calculateStylesMemo,
  convertCoordinatesMemo: convertCoordinatesMemo,
  getDefaultStyle: getDefaultStyle,
  getFeatureAccentedStyle: getFeatureAccentedStyle,
  getFeatureDefaultStyle: getFeatureDefaultStyle,
  getFeatureLeafletStyle: getFeatureLeafletStyle,
  getMarkerShapeCssStyle: getMarkerShapeCssStyle,
  getMarkerShapeSvgStyle: getMarkerShapeSvgStyle
};